import {
  Avatar,
  Box,
  Flex,
  Heading,
  Link,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { useMemo, useState, type ReactNode } from 'react';

import { EXPERT_RATINGS_PREVIEW_ROWS } from './fixtures/marketingPreviewData';
import type { ExpertRatingsPreviewRow } from './fixtures/marketingPreviewData';
import { PrTableCellLite } from './shared/PrTableCellLite';
import { ScoreValueBox } from '../Common/ScoreValueBox';
import { PR_SHORT } from '../../constants/watchdogProfitabilityRating';
import {
  formatRoiPct,
  formatTotalUnitsFromRoi,
  estimatedProfitTextColor,
} from '../../utils/authorProfitCalc';
import { PlatformBadgeLite } from './shared/PlatformBadgeLite';
import { RankByPrExplainer } from './shared/RankByPrExplainer';

const tabularNums = { fontVariantNumeric: 'tabular-nums' } as const;

type SortKey =
  | 'rank'
  | 'profit_rating'
  | 'roi_pct'
  | 'units'
  | 'credibility_score'
  | 'win_percentage'
  | 'total_picks';

type SortDir = 'asc' | 'desc';

const DEFAULT_SORT_KEY: SortKey = 'profit_rating';

/** Flat 1u stake units from ROI% × pick count (same math as formatTotalUnitsFromRoi). */
function totalUnitsFromRoi(roiPct: number, totalPicks: number): number {
  if (
    !Number.isFinite(roiPct) ||
    !Number.isFinite(totalPicks) ||
    totalPicks <= 0
  ) {
    return Number.NEGATIVE_INFINITY;
  }
  return (roiPct / 100) * totalPicks;
}

function nextSortDir(column: SortKey, current: SortKey, dir: SortDir): SortDir {
  if (column !== current) {
    return 'desc';
  }
  return dir === 'asc' ? 'desc' : 'asc';
}

function compareSortValues(
  left: string | number,
  right: string | number,
  dir: SortDir,
): number {
  if (typeof left === 'string' && typeof right === 'string') {
    return dir === 'asc'
      ? left.localeCompare(right, undefined, { sensitivity: 'base' })
      : right.localeCompare(left, undefined, { sensitivity: 'base' });
  }
  const a = Number(left);
  const b = Number(right);
  return dir === 'asc' ? a - b : b - a;
}

function sortPreviewRows(
  rows: ExpertRatingsPreviewRowWithLink[],
  sortKey: SortKey,
  sortDir: SortDir,
): ExpertRatingsPreviewRowWithLink[] {
  const sorted = [...rows].sort((left, right) => {
    switch (sortKey) {
      case 'profit_rating':
        return compareSortValues(
          left.profit_rating,
          right.profit_rating,
          sortDir,
        );
      case 'roi_pct':
        return compareSortValues(left.roi_pct, right.roi_pct, sortDir);
      case 'units':
        return compareSortValues(
          totalUnitsFromRoi(left.roi_pct, left.total_picks),
          totalUnitsFromRoi(right.roi_pct, right.total_picks),
          sortDir,
        );
      case 'credibility_score':
        return compareSortValues(
          left.credibility_score,
          right.credibility_score,
          sortDir,
        );
      case 'win_percentage':
        return compareSortValues(
          left.win_percentage,
          right.win_percentage,
          sortDir,
        );
      case 'total_picks':
        return compareSortValues(left.total_picks, right.total_picks, sortDir);
      case 'rank':
      default:
        return compareSortValues(left.rank, right.rank, sortDir);
    }
  });
  return sorted;
}

function StaticHeaderTh({
  children,
  textAlign,
  headerBg,
}: {
  children: ReactNode;
  textAlign?: 'left' | 'center' | 'right';
  headerBg: string;
}) {
  return (
    <Th textAlign={textAlign} px={2} bg={headerBg}>
      {children}
    </Th>
  );
}

function SortableTh({
  children,
  column,
  activeColumn,
  direction,
  onSort,
  textAlign,
  headerBg,
}: {
  children: ReactNode;
  column: SortKey;
  activeColumn: SortKey;
  direction: SortDir;
  onSort: (column: SortKey) => void;
  textAlign?: 'left' | 'center' | 'right';
  headerBg: string;
}) {
  const isActive = activeColumn === column;
  return (
    <Th
      textAlign={textAlign}
      px={2}
      cursor="pointer"
      userSelect="none"
      onClick={(event) => {
        event.stopPropagation();
        onSort(column);
      }}
      bg={headerBg}
      _hover={{ color: 'white', filter: 'brightness(1.08)' }}
      aria-sort={
        isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      {children}
      {isActive ? (
        <Text as="span" ml={1} aria-hidden>
          {direction === 'asc' ? '▲' : '▼'}
        </Text>
      ) : null}
    </Th>
  );
}

/** Dual ROI % / Units header; order matches the stacked cell (active metric on top). */
function RoiUnitsSortableTh({
  activeColumn,
  direction,
  onSort,
  headerBg,
}: {
  activeColumn: SortKey;
  direction: SortDir;
  onSort: (column: SortKey) => void;
  headerBg: string;
}) {
  const unitsOnTop = activeColumn === 'units';
  const primary: SortKey = unitsOnTop ? 'units' : 'roi_pct';
  const secondary: SortKey = unitsOnTop ? 'roi_pct' : 'units';
  const primaryLabel = unitsOnTop ? 'Units' : 'ROI %';
  const secondaryLabel = unitsOnTop ? 'ROI %' : 'Units';
  const isActive = activeColumn === 'roi_pct' || activeColumn === 'units';

  return (
    <Th
      textAlign="center"
      px={2}
      userSelect="none"
      bg={headerBg}
      aria-sort={
        isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      <VStack spacing={0} align="center">
        <Text
          as="button"
          type="button"
          cursor="pointer"
          fontSize="inherit"
          fontWeight="inherit"
          textTransform="inherit"
          letterSpacing="inherit"
          color="inherit"
          lineHeight="1.2"
          onClick={(event) => {
            event.stopPropagation();
            onSort(primary);
          }}
          _hover={{ color: 'white', filter: 'brightness(1.08)' }}
        >
          {primaryLabel}
          {activeColumn === primary ? (
            <Text as="span" ml={1} aria-hidden>
              {direction === 'asc' ? '▲' : '▼'}
            </Text>
          ) : null}
        </Text>
        <Text
          as="button"
          type="button"
          cursor="pointer"
          fontSize="2xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wider"
          color="gray.400"
          lineHeight="1.2"
          onClick={(event) => {
            event.stopPropagation();
            onSort(secondary);
          }}
          _hover={{ color: 'white' }}
        >
          {secondaryLabel}
          {activeColumn === secondary ? (
            <Text as="span" ml={1} aria-hidden>
              {direction === 'asc' ? '▲' : '▼'}
            </Text>
          ) : null}
        </Text>
      </VStack>
    </Th>
  );
}

type ExpertRatingsPreviewRowWithLink = ExpertRatingsPreviewRow & {
  profile_url?: string;
};

function ExpertName({
  row,
  nameColor,
  linkDisabled = false,
  textAlign = 'left',
}: {
  row: ExpertRatingsPreviewRowWithLink;
  nameColor: string;
  linkDisabled?: boolean;
  textAlign?: 'left' | 'right';
}) {
  const label = (
    <Text
      fontWeight="semibold"
      fontSize="sm"
      color={nameColor}
      noOfLines={1}
      textAlign={textAlign}
      _hover={row.profile_url ? { color: 'primary.400' } : undefined}
    >
      {row.author_name}
    </Text>
  );
  if (!row.profile_url || linkDisabled) return label;
  return (
    <Link
      href={row.profile_url}
      isExternal
      target="_blank"
      rel="noopener noreferrer"
      _hover={{ textDecoration: 'none' }}
      display="block"
    >
      {label}
    </Link>
  );
}

function RatingsTableRowLite({
  row,
  rowHref,
  rankAccent = false,
  sortKey = DEFAULT_SORT_KEY,
}: {
  row: ExpertRatingsPreviewRowWithLink;
  rowHref?: string;
  rankAccent?: boolean;
  sortKey?: SortKey;
}) {
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.80');
  const nameColor = useColorModeValue('gray.900', 'white');
  const rankColor = useColorModeValue('primary.600', 'primary.300');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const wcsDisplay = row.credibility_score.toFixed(1);
  const roiPctDisplay = formatRoiPct(row.roi_pct);
  const unitsDisplay = formatTotalUnitsFromRoi(row.roi_pct, row.total_picks);
  const unitsOnTop = sortKey === 'units';
  const profitColor = estimatedProfitTextColor(row.roi_pct, nameColor);

  const goToRowHref = rowHref
    ? () => {
        window.location.assign(rowHref);
      }
    : undefined;

  return (
    <Tr
      _hover={{ bg: rowHoverBg }}
      cursor={rowHref ? 'pointer' : undefined}
      onClick={goToRowHref}
      role={rowHref ? 'link' : undefined}
      tabIndex={rowHref ? 0 : undefined}
      onKeyDown={
        rowHref
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                window.location.assign(rowHref);
              }
            }
          : undefined
      }
    >
      <Td textAlign="center" px={2} sx={tabularNums}>
        <Text
          fontWeight="extrabold"
          fontSize={rankAccent ? 'md' : 'sm'}
          color={rankAccent ? rankColor : nameColor}
        >
          {row.rank}
        </Text>
      </Td>
      <Td textAlign="center" px={2}>
        <Flex justify="center" w="full">
          <PrTableCellLite score={row.profit_rating} />
        </Flex>
      </Td>
      <Td textAlign="center" px={2} sx={tabularNums}>
        {unitsOnTop ? (
          <>
            <Text fontSize="sm" fontWeight="semibold" color={profitColor}>
              {unitsDisplay}
            </Text>
            <Text fontSize="2xs" color={muted}>
              {roiPctDisplay}
            </Text>
          </>
        ) : (
          <>
            <Text fontSize="sm" fontWeight="semibold" color={profitColor}>
              {roiPctDisplay}
            </Text>
            <Text fontSize="2xs" color={muted}>
              {unitsDisplay}
            </Text>
          </>
        )}
      </Td>
      <Td textAlign="center" px={2}>
        <Flex justify="center">
          <ScoreValueBox
            family="win_rate"
            shortLabel="WCS"
            value={wcsDisplay}
            size="xs"
          />
        </Flex>
      </Td>
      <Td px={2}>
        <Flex align="center" gap={2} minW={0}>
          <Avatar size="sm" name={row.author_name} flexShrink={0} />
          <Flex align="center" gap={1.5} minW={0} wrap="wrap">
            <ExpertName
              row={row}
              nameColor={nameColor}
              linkDisabled={!!rowHref}
            />
            <PlatformBadgeLite authorSource={row.author_source} />
          </Flex>
        </Flex>
      </Td>
      <Td textAlign="center" px={2} sx={tabularNums}>
        <Text fontSize="sm" fontWeight="semibold" color={nameColor}>
          {row.win_percentage.toFixed(1)}%
        </Text>
        <Text fontSize="2xs" color={muted}>
          {row.total_picks} picks
        </Text>
      </Td>
    </Tr>
  );
}

/** Metric shown top-right; defaults to PR (matches default sort). */
type MobileFeaturedMetric =
  | 'profit_rating'
  | 'roi_pct'
  | 'units'
  | 'credibility_score'
  | 'win_percentage'
  | 'total_picks';

function featuredMetricFromSortKey(sortKey: SortKey): MobileFeaturedMetric {
  switch (sortKey) {
    case 'roi_pct':
    case 'units':
    case 'credibility_score':
    case 'win_percentage':
    case 'total_picks':
      return sortKey;
    case 'profit_rating':
    case 'rank':
    default:
      return 'profit_rating';
  }
}

function MobileStatCell({
  label,
  value,
  valueColor,
  muted,
  borderColor,
  showRightBorder = false,
  children,
}: {
  label: string;
  value?: string;
  valueColor?: string;
  muted: string;
  borderColor: string;
  showRightBorder?: boolean;
  children?: ReactNode;
}) {
  return (
    <Box
      minW={0}
      textAlign="center"
      px={1.5}
      py={1.5}
      borderRightWidth={showRightBorder ? '1px' : '0'}
      borderColor={borderColor}
    >
      <Text
        fontSize="2xs"
        fontWeight="bold"
        color={muted}
        textTransform="uppercase"
        letterSpacing="wider"
        mb={0.5}
        lineHeight="1.2"
      >
        {label}
      </Text>
      {children ?? (
        <Text
          fontSize="xs"
          fontWeight="semibold"
          color={valueColor}
          sx={tabularNums}
          noOfLines={1}
          lineHeight="1.2"
        >
          {value}
        </Text>
      )}
    </Box>
  );
}

function RatingsMobileCardLite({
  row,
  rowHref,
  polished = false,
  sortKey = DEFAULT_SORT_KEY,
}: {
  row: ExpertRatingsPreviewRowWithLink;
  rowHref?: string;
  polished?: boolean;
  sortKey?: SortKey;
}) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const gridBg = useColorModeValue('gray.50', 'blackAlpha.300');
  const nameColor = useColorModeValue('gray.900', 'white');
  const rankColor = useColorModeValue('primary.600', 'primary.300');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const featuredAccent = useColorModeValue('gray.300', 'whiteAlpha.300');
  const roiPctDisplay = formatRoiPct(row.roi_pct);
  const unitsDisplay = formatTotalUnitsFromRoi(row.roi_pct, row.total_picks);
  const wcsDisplay = row.credibility_score.toFixed(1);
  const featured = featuredMetricFromSortKey(sortKey);
  const picksDisplay = row.total_picks.toLocaleString();
  const winDisplay = `${row.win_percentage.toFixed(1)}%`;

  const featuredShellProps = {
    flexShrink: 0,
    textAlign: 'center' as const,
    px: 1,
    pb: 1,
    borderBottomWidth: '2px',
    borderBottomColor: featuredAccent,
  };

  const featuredNode = (() => {
    switch (featured) {
      case 'credibility_score':
        return (
          <Box {...featuredShellProps}>
            <ScoreValueBox
              family="win_rate"
              shortLabel="WCS"
              value={wcsDisplay}
              size="sm"
            />
          </Box>
        );
      case 'roi_pct':
        return (
          <Box {...featuredShellProps}>
            <Text
              fontSize="2xs"
              fontWeight="bold"
              color={muted}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              ROI %
            </Text>
            <Text
              fontSize="lg"
              fontWeight="extrabold"
              color={estimatedProfitTextColor(row.roi_pct, nameColor)}
              sx={tabularNums}
              lineHeight="1.1"
            >
              {roiPctDisplay}
            </Text>
            <Text fontSize="2xs" color={muted} sx={tabularNums}>
              {unitsDisplay}
            </Text>
          </Box>
        );
      case 'units':
        return (
          <Box {...featuredShellProps}>
            <Text
              fontSize="2xs"
              fontWeight="bold"
              color={muted}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Units
            </Text>
            <Text
              fontSize="lg"
              fontWeight="extrabold"
              color={estimatedProfitTextColor(row.roi_pct, nameColor)}
              sx={tabularNums}
              lineHeight="1.1"
            >
              {unitsDisplay}
            </Text>
            <Text fontSize="2xs" color={muted} sx={tabularNums}>
              {roiPctDisplay}
            </Text>
          </Box>
        );
      case 'win_percentage':
        return (
          <Box {...featuredShellProps}>
            <Text
              fontSize="2xs"
              fontWeight="bold"
              color={muted}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Win %
            </Text>
            <Text
              fontSize="lg"
              fontWeight="extrabold"
              color={nameColor}
              sx={tabularNums}
              lineHeight="1.1"
            >
              {winDisplay}
            </Text>
          </Box>
        );
      case 'total_picks':
        return (
          <Box {...featuredShellProps}>
            <Text
              fontSize="2xs"
              fontWeight="bold"
              color={muted}
              textTransform="uppercase"
              letterSpacing="wider"
            >
              Picks
            </Text>
            <Text
              fontSize="lg"
              fontWeight="extrabold"
              color={nameColor}
              sx={tabularNums}
              lineHeight="1.1"
            >
              {picksDisplay}
            </Text>
          </Box>
        );
      case 'profit_rating':
      default:
        return (
          <Box {...featuredShellProps}>
            <PrTableCellLite score={row.profit_rating} size="sm" />
          </Box>
        );
    }
  })();

  type DetailSpec = {
    key: string;
    label: string;
    value?: string;
    valueColor?: string;
    children?: ReactNode;
  };
  const detailSpecs: DetailSpec[] = [];
  if (featured !== 'profit_rating') {
    detailSpecs.push({
      key: 'pr',
      label: 'PR',
      children: (
        <Flex justify="center">
          <PrTableCellLite score={row.profit_rating} hideShortLabel />
        </Flex>
      ),
    });
  }
  if (featured !== 'roi_pct' && featured !== 'units') {
    detailSpecs.push({
      key: 'roi',
      label: 'ROI %',
      value: roiPctDisplay,
      valueColor: estimatedProfitTextColor(row.roi_pct, nameColor),
    });
  }
  if (featured !== 'total_picks') {
    detailSpecs.push({
      key: 'picks',
      label: 'Picks',
      value: picksDisplay,
      valueColor: nameColor,
    });
  }
  if (featured !== 'win_percentage') {
    detailSpecs.push({
      key: 'win',
      label: 'Win %',
      value: winDisplay,
      valueColor: nameColor,
    });
  }
  const showWcsBottom = featured !== 'credibility_score';

  return (
    <Box
      as={rowHref ? Link : undefined}
      href={rowHref}
      isExternal={!!rowHref}
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderLeftWidth={polished ? '3px' : '1px'}
      borderLeftColor={polished ? rankColor : borderColor}
      borderRadius="lg"
      px={3}
      py={3}
      cursor={rowHref ? 'pointer' : undefined}
      display="block"
      minW={0}
      maxW="100%"
      overflow="hidden"
      minH="44px"
      boxShadow={polished ? 'sm' : undefined}
      transition={
        polished ? 'border-color 0.15s ease, box-shadow 0.15s ease' : undefined
      }
      _hover={
        rowHref
          ? {
              textDecoration: 'none',
              borderColor: polished ? rankColor : 'primary.500',
              boxShadow: polished ? 'md' : undefined,
            }
          : undefined
      }
      sx={
        rowHref
          ? { contentVisibility: 'auto', containIntrinsicSize: '0 96px' }
          : undefined
      }
    >
      <Flex align="flex-start" justify="space-between" gap={3} mb={2} minW={0}>
        <Box flex={1} minW={0}>
          <Text
            fontWeight="extrabold"
            fontSize="lg"
            color={rankColor}
            sx={tabularNums}
            lineHeight="1.1"
          >
            #{row.rank}
          </Text>
          <Flex align="center" gap={1.5} minW={0} mt={0.5}>
            <Box minW={0} flexShrink={1}>
              <ExpertName
                row={row}
                nameColor={nameColor}
                linkDisabled={!!rowHref}
                textAlign="left"
              />
            </Box>
            <PlatformBadgeLite authorSource={row.author_source} />
          </Flex>
        </Box>
        <Box flexShrink={0}>{featuredNode}</Box>
      </Flex>
      <Flex align="stretch" gap={2} minW={0}>
        {detailSpecs.length > 0 ? (
          <Box
            flex={1}
            minW={0}
            display="grid"
            gridTemplateColumns={`repeat(${detailSpecs.length}, minmax(0, 1fr))`}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="md"
            bg={gridBg}
            overflow="hidden"
          >
            {detailSpecs.map((spec, index) => (
              <MobileStatCell
                key={spec.key}
                label={spec.label}
                value={spec.value}
                valueColor={spec.valueColor}
                muted={muted}
                borderColor={borderColor}
                showRightBorder={index < detailSpecs.length - 1}
              >
                {spec.children}
              </MobileStatCell>
            ))}
          </Box>
        ) : (
          <Box flex={1} />
        )}
        {showWcsBottom ? (
          <Flex flexShrink={0} align="center">
            <ScoreValueBox
              family="win_rate"
              shortLabel="WCS"
              value={wcsDisplay}
              size="xs"
            />
          </Flex>
        ) : null}
      </Flex>
    </Box>
  );
}

type ExpertRatingsPreviewProps = {
  rows?: ExpertRatingsPreviewRowWithLink[];
  maxRows?: number;
  isLoading?: boolean;
  showHeading?: boolean;
  /** When set, each row navigates to this URL (e.g. app signup on /ratings). */
  rowHref?: string;
  /** Tighter rows for long scrollable leaderboards. */
  compact?: boolean;
  /** Sticky column headers when embedded in a scroll container. */
  stickyHeader?: boolean;
  /** Avoid clipping sticky headers inside a parent scroll box. */
  inScrollContainer?: boolean;
  /** Client-side column sorting (no API). */
  sortable?: boolean;
  /** Larger, higher-contrast table headers (homepage preview). */
  prominentHeaders?: boolean;
  /** Always use mobile card rows (e.g. hero teaser in a narrow panel). */
  forceCards?: boolean;
  /** Skip RankByPrExplainer when the parent already shows one. */
  hideRankExplainer?: boolean;
};

export function ExpertRatingsPreview({
  rows: rowsProp,
  maxRows,
  isLoading = false,
  showHeading = true,
  rowHref,
  compact = false,
  stickyHeader = false,
  inScrollContainer = false,
  sortable = false,
  prominentHeaders = false,
  forceCards = false,
  hideRankExplainer = false,
}: ExpertRatingsPreviewProps = {}) {
  const [sortKey, setSortKey] = useState<SortKey>(DEFAULT_SORT_KEY);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (column: SortKey) => {
    setSortDir((currentDir) => nextSortDir(column, sortKey, currentDir));
    setSortKey(column);
  };

  const fallbackRows = EXPERT_RATINGS_PREVIEW_ROWS;
  const sourceRows = rowsProp !== undefined ? rowsProp : fallbackRows;
  const baseRows = maxRows ? sourceRows.slice(0, maxRows) : sourceRows;
  const rows = useMemo(() => {
    if (!sortable) return baseRows;
    return sortPreviewRows(baseRows, sortKey, sortDir);
  }, [baseRows, sortable, sortKey, sortDir]);
  const headingColor = useColorModeValue('gray.900', 'white');
  const subtitleColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowBg = useColorModeValue('white', 'gray.800');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const prominentHeaderColor = useColorModeValue('gray.700', 'gray.100');
  const headerBg = useColorModeValue('gray.100', 'gray.800');
  const stickyHeaderBg = useColorModeValue('gray.100', 'gray.700');
  const emphasizedHeaderBg = useColorModeValue('gray.50', 'blackAlpha.400');
  const listBg = useColorModeValue('gray.50', 'blackAlpha.250');
  const zebraBg = useColorModeValue('gray.50', 'whiteAlpha.30');
  const headerBorderAccent = useColorModeValue('primary.200', 'primary.600');
  const sortBarBg = useColorModeValue('gray.100', 'whiteAlpha.80');
  const breakpointMobile =
    useBreakpointValue({ base: true, md: false }) ?? true;
  const isMobile = forceCards || breakpointMobile;
  const rowPy = compact ? 2 : 4;
  const emphasizeHeaders = prominentHeaders || sortable;
  const headerPy = emphasizeHeaders ? { base: 3, md: 4 } : compact ? 2 : 3;
  const headerFontSize = emphasizeHeaders ? { base: 'xs', md: 'sm' } : 'xs';
  const headerTextColor = emphasizeHeaders ? prominentHeaderColor : muted;
  const rowIntrinsicSize = compact ? '0 52px' : '0 72px';
  const mobileSpacing = compact ? 2 : 3;
  const headerCellBg = stickyHeader
    ? stickyHeaderBg
    : emphasizeHeaders
      ? emphasizedHeaderBg
      : headerBg;

  const desktopTable = (
    <Table
      size="sm"
      variant="simple"
      sx={{
        'th, td': { borderColor, verticalAlign: 'middle' },
        th: {
          bg: headerCellBg,
          borderBottomWidth: '2px',
          borderBottomColor: emphasizeHeaders
            ? headerBorderAccent
            : borderColor,
          fontSize: headerFontSize,
          fontWeight: 'bold',
          color: headerTextColor,
          textTransform: 'uppercase',
          letterSpacing: 'wider',
          py: headerPy,
        },
        'tbody tr': {
          bg: rowBg,
          ...(emphasizeHeaders
            ? {
                '&:nth-of-type(even)': { bg: zebraBg },
              }
            : {}),
          ...(stickyHeader
            ? {}
            : {
                contentVisibility: 'auto',
                containIntrinsicSize: rowIntrinsicSize,
              }),
        },
        'tbody td': {
          borderBottomWidth: '1px',
          py: rowPy,
          px: compact ? 3 : 2,
        },
        'tbody tr:last-of-type td': { borderBottomWidth: 0 },
      }}
    >
      <Thead
        {...(stickyHeader
          ? {
              position: 'sticky',
              top: 0,
              zIndex: 3,
              bg: headerCellBg,
              boxShadow: emphasizeHeaders
                ? '0 4px 12px rgba(0, 0, 0, 0.45)'
                : '0 2px 6px rgba(0, 0, 0, 0.35)',
            }
          : {})}
      >
        <Tr>
          {sortable ? (
            <>
              <SortableTh
                column="rank"
                activeColumn={sortKey}
                direction={sortDir}
                onSort={handleSort}
                textAlign="center"
                headerBg={headerCellBg}
              >
                #
              </SortableTh>
              <SortableTh
                column="profit_rating"
                activeColumn={sortKey}
                direction={sortDir}
                onSort={handleSort}
                textAlign="center"
                headerBg={headerCellBg}
              >
                {PR_SHORT}
              </SortableTh>
              <RoiUnitsSortableTh
                activeColumn={sortKey}
                direction={sortDir}
                onSort={handleSort}
                headerBg={headerCellBg}
              />
              <SortableTh
                column="credibility_score"
                activeColumn={sortKey}
                direction={sortDir}
                onSort={handleSort}
                textAlign="center"
                headerBg={headerCellBg}
              >
                WCS
              </SortableTh>
              <StaticHeaderTh headerBg={headerCellBg}>Expert</StaticHeaderTh>
              <SortableTh
                column="win_percentage"
                activeColumn={sortKey}
                direction={sortDir}
                onSort={handleSort}
                textAlign="center"
                headerBg={headerCellBg}
              >
                Win %
              </SortableTh>
            </>
          ) : (
            <>
              <StaticHeaderTh textAlign="center" headerBg={headerCellBg}>
                Rank
              </StaticHeaderTh>
              <StaticHeaderTh textAlign="center" headerBg={headerCellBg}>
                {PR_SHORT}
              </StaticHeaderTh>
              <StaticHeaderTh textAlign="center" headerBg={headerCellBg}>
                ROI %
              </StaticHeaderTh>
              <StaticHeaderTh textAlign="center" headerBg={headerCellBg}>
                WCS
              </StaticHeaderTh>
              <StaticHeaderTh headerBg={headerCellBg}>Expert</StaticHeaderTh>
              <StaticHeaderTh textAlign="center" headerBg={headerCellBg}>
                Win %
              </StaticHeaderTh>
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <RatingsTableRowLite
            key={`${row.author_name}-${row.rank}`}
            row={row}
            rowHref={rowHref}
            rankAccent={emphasizeHeaders}
            sortKey={sortable ? sortKey : DEFAULT_SORT_KEY}
          />
        ))}
      </Tbody>
    </Table>
  );

  return (
    <Box w="100%" minW={0} overflowX="hidden">
      {showHeading ? (
        <VStack align="stretch" spacing={2} mb={{ base: 4, md: 5 }}>
          <Heading
            as="h3"
            fontSize={{ base: 'xl', md: '2xl' }}
            fontWeight="extrabold"
            color={headingColor}
          >
            Expert Ratings
          </Heading>
          <Text
            fontSize={{ base: 'sm', md: 'md' }}
            fontWeight="medium"
            color={subtitleColor}
            lineHeight="1.6"
          >
            Ranked by {PR_SHORT} (modeled tailing profit). WCS shows win-rate
            skill separately — a high win rate at bad prices can mean strong WCS
            but weaker {PR_SHORT}.
          </Text>
        </VStack>
      ) : null}

      {emphasizeHeaders && !hideRankExplainer ? <RankByPrExplainer /> : null}

      {isLoading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" color="primary.400" />
        </Flex>
      ) : (
        <VStack align="stretch" spacing={mobileSpacing} minW={0} w="100%">
          {sortable ? (
            <Flex
              gap={2}
              align="center"
              flexWrap="wrap"
              p={3}
              borderRadius="lg"
              bg={sortBarBg}
              borderWidth="1px"
              borderColor={borderColor}
              minW={0}
              w="100%"
            >
              <Select
                size="sm"
                value={sortKey}
                onChange={(event) => {
                  const nextKey = event.target.value as SortKey;
                  setSortKey(nextKey);
                  setSortDir('desc');
                }}
                bg="gray.700"
                borderColor="whiteAlpha.200"
                maxW={{ base: '100%', sm: '220px' }}
                flex={{ base: '1 1 140px', sm: '0 1 auto' }}
                minW={0}
                aria-label="Sort experts by"
              >
                <option value="profit_rating">Profit Rating</option>
                <option value="roi_pct">ROI %</option>
                <option value="units">Units</option>
                <option value="credibility_score">WCS</option>
                <option value="win_percentage">Win %</option>
                <option value="total_picks">Pick count</option>
                <option value="rank">Rank</option>
              </Select>
              <Select
                size="sm"
                value={sortDir}
                onChange={(event) => setSortDir(event.target.value as SortDir)}
                bg="gray.700"
                borderColor="whiteAlpha.200"
                maxW={{ base: '100%', sm: '140px' }}
                flex={{ base: '1 1 120px', sm: '0 1 auto' }}
                minW={0}
                aria-label="Sort direction"
              >
                <option value="desc">High → low</option>
                <option value="asc">Low → high</option>
              </Select>
            </Flex>
          ) : null}
          {isMobile ? (
            rows.map((row) => (
              <RatingsMobileCardLite
                key={`${row.author_name}-${row.rank}`}
                row={row}
                rowHref={rowHref}
                polished={emphasizeHeaders}
                sortKey={sortable ? sortKey : DEFAULT_SORT_KEY}
              />
            ))
          ) : (
            <Box
              borderRadius="12px"
              overflow={inScrollContainer ? 'visible' : 'hidden'}
              borderWidth="1px"
              borderColor={borderColor}
              bg={listBg}
            >
              {inScrollContainer ? (
                desktopTable
              ) : (
                <Box overflowX="auto">{desktopTable}</Box>
              )}
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
}
